import React, { useEffect, useMemo, useRef, useState } from 'react';
import { discussionsApi } from '../../services/api';
import { getSocket } from '../../services/socket';
import toast from 'react-hot-toast';
import {
  HiOutlineArrowLeft,
  HiOutlineDocumentText,
  HiOutlineFilm,
  HiOutlinePaperAirplane,
  HiOutlinePaperClip,
  HiOutlinePhotograph,
  HiOutlinePencil,
  HiOutlinePlus,
  HiOutlineTrash,
  HiOutlineUserGroup,
  HiOutlineUsers,
  HiX,
} from 'react-icons/hi';
import { useAuth } from '../../context/AuthContext';

const MD_BREAKPOINT = 768;

function useIsNarrowScreen() {
  const [narrow, setNarrow] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(`(max-width: ${MD_BREAKPOINT - 1}px)`).matches : false
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${MD_BREAKPOINT - 1}px)`);
    const onChange = () => setNarrow(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return narrow;
}

const isImageAttachment = (file) => {
  const mime = String(file?.mime_type || '').toLowerCase();
  if (mime.startsWith('image/')) return true;
  const name = String(file?.original_name || '').toLowerCase();
  return /\.(jpe?g|png|gif|webp|bmp|svg|heic|heif)$/.test(name);
};

const isVideoAttachment = (file) => {
  const mime = String(file?.mime_type || '').toLowerCase();
  if (mime.startsWith('video/')) return true;
  const name = String(file?.original_name || '').toLowerCase();
  return /\.(mp4|webm|ogg|mov|m4v|avi|mkv|3gp)$/.test(name);
};

const PendingAttachmentPreview = ({ file }) => {
  if (isImageAttachment(file) && file.signed_url) {
    return (
      <img
        src={file.signed_url}
        alt={file.original_name || 'Preview'}
        className="h-20 w-20 rounded object-cover"
      />
    );
  }
  if (isVideoAttachment(file) && file.signed_url) {
    return (
      <video
        src={file.signed_url}
        className="h-20 w-20 rounded object-cover bg-black"
        muted
        playsInline
        preload="metadata"
      />
    );
  }
  return <div className="rounded px-2 py-1 text-xs text-gray-700">{file.original_name}</div>;
};

const MessageAttachment = ({ file, isOwn }) => {
  if (isImageAttachment(file) && file.signed_url) {
    return (
      <a href={file.signed_url} target="_blank" rel="noreferrer" className="mt-1 block">
        <img
          src={file.signed_url}
          alt={file.original_name || 'Image'}
          className="max-h-64 max-w-full rounded-lg object-contain"
          loading="lazy"
        />
      </a>
    );
  }

  if (isVideoAttachment(file) && file.signed_url) {
    return (
      <div className="mt-1">
        <video
          controls
          playsInline
          preload="metadata"
          className="max-h-64 max-w-full rounded-lg bg-black"
        >
          <source src={file.signed_url} type={file.mime_type || 'video/mp4'} />
          Your browser does not support video playback.
        </video>
        <a
          href={file.signed_url}
          target="_blank"
          rel="noreferrer"
          className={`mt-1 block text-xs underline ${isOwn ? 'text-primary-100' : 'text-primary-700'}`}
        >
          {file.original_name || 'Download video'}
        </a>
      </div>
    );
  }

  return (
    <a
      href={file.signed_url || '#'}
      target="_blank"
      rel="noreferrer"
      className={`block break-all text-xs underline ${
        isOwn ? 'text-white' : 'text-primary-700'
      }`}
    >
      {file.original_name || 'Attachment'}
    </a>
  );
};

const getChannelLabel = (channel) => {
  if (!channel) return 'Chat';
  if (channel.display_name) return channel.display_name;
  if (channel.name) return channel.name;
  return channel.channel_type === 'direct' ? 'Direct chat' : `Channel #${channel.id}`;
};

const Discussions = () => {
  const { user } = useAuth();
  const isNarrow = useIsNarrowScreen();
  /** On phone: 'list' = inbox only, 'chat' = active thread (WhatsApp-style). */
  const [mobileScreen, setMobileScreen] = useState('list');
  const [channels, setChannels] = useState([]);
  const [activeChannelId, setActiveChannelId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeMode, setComposeMode] = useState('direct');
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [groupName, setGroupName] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [activeMembers, setActiveMembers] = useState([]);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionOptions, setMentionOptions] = useState([]);
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [deletingGroup, setDeletingGroup] = useState(false);
  const [editingGroupName, setEditingGroupName] = useState(false);
  const [groupNameDraft, setGroupNameDraft] = useState('');
  const [renamingGroup, setRenamingGroup] = useState(false);
  const [attachMenuOpen, setAttachMenuOpen] = useState(false);
  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const documentInputRef = useRef(null);
  const attachMenuRef = useRef(null);

  const activeChannel = useMemo(
    () => channels.find((c) => c.id === activeChannelId) || null,
    [channels, activeChannelId]
  );

  const canManageActiveGroup = useMemo(() => {
    if (!activeChannel || activeChannel.channel_type !== 'group' || !user?.id) return false;
    const membership = activeMembers.find((m) => Number(m.id) === Number(user.id));
    return membership?.member_role === 'owner' || membership?.member_role === 'admin';
  }, [activeChannel, activeMembers, user?.id]);

  const canDeleteActiveGroup = canManageActiveGroup;

  const canDeleteActiveChat = useMemo(() => {
    if (!activeChannel) return false;
    if (activeChannel.channel_type === 'direct') return true;
    return canDeleteActiveGroup;
  }, [activeChannel, canDeleteActiveGroup]);

  const activeChannelLabel = useMemo(() => getChannelLabel(activeChannel), [activeChannel]);

  const loadChannels = async () => {
    const res = await discussionsApi.getChannels();
    const list = res.data.data || [];
    setChannels(list);
    setActiveChannelId((prev) => {
      if (prev != null) return prev;
      if (!list.length) return null;
      const startListOnly =
        typeof window !== 'undefined' && window.matchMedia(`(max-width: ${MD_BREAKPOINT - 1}px)`).matches;
      if (startListOnly) return null;
      return list[0].id;
    });
  };

  const loadUsers = async () => {
    const res = await discussionsApi.getUsers();
    setUsers(res.data.data || []);
  };

  const loadMessages = async (channelId) => {
    if (!channelId) return;
    const res = await discussionsApi.getMessages(channelId);
    setMessages(res.data.data || []);
  };

  const loadMembers = async (channelId) => {
    if (!channelId) {
      setActiveMembers([]);
      return;
    }
    const res = await discussionsApi.getChannelMembers(channelId);
    setActiveMembers(res.data.data || []);
  };

  useEffect(() => {
    (async () => {
      try {
        await loadChannels();
        await loadUsers();
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!activeChannelId) {
      setMessages([]);
      setActiveMembers([]);
      return;
    }
    setEditingGroupName(false);
    setGroupNameDraft('');
    loadMessages(activeChannelId);
    loadMembers(activeChannelId);
    const socket = getSocket();
    if (!socket) return;
    socket.emit('discussion:join_channel', activeChannelId);
    const onNewMessage = (payload) => {
      if (Number(payload.channel_id) === Number(activeChannelId)) {
        setMessages((prev) => {
          if (prev.some((m) => Number(m.id) === Number(payload.id))) return prev;
          return [...prev, payload];
        });
      }
      loadChannels();
    };
    const onChannelDeleted = (payload) => {
      if (Number(payload.channel_id) !== Number(activeChannelId)) return;
      setActiveChannelId(null);
      setMessages([]);
      setActiveMembers([]);
      if (isNarrow) setMobileScreen('list');
      loadChannels();
      toast('This group was deleted');
    };
    const onChannelUpdated = (payload) => {
      setChannels((prev) =>
        prev.map((c) =>
          Number(c.id) === Number(payload.channel_id)
            ? { ...c, name: payload.name, display_name: payload.name }
            : c
        )
      );
    };
    socket.on('message:new', onNewMessage);
    socket.on('channel:deleted', onChannelDeleted);
    socket.on('channel:updated', onChannelUpdated);
    return () => {
      socket.emit('discussion:leave_channel', activeChannelId);
      socket.off('message:new', onNewMessage);
      socket.off('channel:deleted', onChannelDeleted);
      socket.off('channel:updated', onChannelUpdated);
    };
  }, [activeChannelId, isNarrow]);

  const sendMessage = async () => {
    const value = text.trim();
    if (!activeChannelId || (!value && pendingAttachments.length === 0)) return;
    const mentionIds = Array.from(value.matchAll(/@\[[^\]]+\]\((\d+)\)/g)).map((m) => Number(m[1]));
    const allImages =
      pendingAttachments.length > 0 &&
      pendingAttachments.every((file) => isImageAttachment(file));
    await discussionsApi.sendMessage(activeChannelId, {
      content_text: value,
      message_type: allImages ? 'image' : pendingAttachments.length > 0 ? 'file' : 'text',
      mention_user_ids: mentionIds,
      attachments: pendingAttachments,
    });
    setText('');
    setPendingAttachments([]);
    setMentionOpen(false);
    await loadMessages(activeChannelId);
  };

  const renderMessageText = (value) => String(value || '').replace(/@\[(.*?)\]\(\d+\)/g, '@$1');

  const onTextChange = (nextText) => {
    setText(nextText);
    const match = nextText.match(/(?:^|\s)@([a-zA-Z0-9._-]*)$/);
    if (!match) {
      setMentionOpen(false);
      return;
    }
    const query = (match[1] || '').toLowerCase();
    const options = activeMembers
      .filter((m) => !query || (m.full_name || '').toLowerCase().includes(query))
      .slice(0, 8);
    setMentionOptions(options);
    setMentionOpen(options.length > 0);
  };

  const onSelectMention = (member) => {
    const updated = text.replace(/@([a-zA-Z0-9._-]*)$/, `@[${member.full_name}](${member.id}) `);
    setText(updated);
    setMentionOpen(false);
  };

  const onPickAttachment = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !activeChannelId) return;
    setAttachMenuOpen(false);
    setUploadingAttachment(true);
    try {
      const res = await discussionsApi.uploadAttachment(activeChannelId, file);
      const payload = res.data?.data;
      if (payload?.object_key) {
        setPendingAttachments((prev) => [...prev, payload]);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to upload attachment');
    } finally {
      setUploadingAttachment(false);
      event.target.value = '';
    }
  };

  const openAttachmentPicker = (type) => {
    setAttachMenuOpen(false);
    if (type === 'photo') photoInputRef.current?.click();
    else if (type === 'video') videoInputRef.current?.click();
    else documentInputRef.current?.click();
  };

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => (u.full_name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q));
  }, [users, userSearch]);

  const toggleUser = (userId) => {
    setSelectedUsers((prev) => (prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]));
  };

  const createConversation = async () => {
    if (composeMode === 'direct') {
      if (selectedUsers.length !== 1) return;
      const res = await discussionsApi.createChannel({
        channel_type: 'direct',
        member_ids: selectedUsers,
      });
      const channelId = res.data?.data?.id;
      await loadChannels();
      if (channelId) {
        setActiveChannelId(channelId);
        if (typeof window !== 'undefined' && window.matchMedia(`(max-width: ${MD_BREAKPOINT - 1}px)`).matches) {
          setMobileScreen('chat');
        }
      }
      setComposeOpen(false);
      setSelectedUsers([]);
      return;
    }
    if (!groupName.trim() || selectedUsers.length < 2) return;
    const res = await discussionsApi.createChannel({
      channel_type: 'group',
      name: groupName.trim(),
      member_ids: selectedUsers,
    });
    const channelId = res.data?.data?.id;
    await loadChannels();
    if (channelId) {
      setActiveChannelId(channelId);
      if (typeof window !== 'undefined' && window.matchMedia(`(max-width: ${MD_BREAKPOINT - 1}px)`).matches) {
        setMobileScreen('chat');
      }
    }
    setComposeOpen(false);
    setGroupName('');
    setSelectedUsers([]);
  };

  const openChannel = (channelId) => {
    setActiveChannelId(channelId);
    if (isNarrow) setMobileScreen('chat');
  };

  const backToChannelList = () => {
    setMobileScreen('list');
  };

  const handleDeleteChat = async () => {
    if (!activeChannelId || !canDeleteActiveChat) return;
    const chatLabel = activeChannelLabel;
    const isGroup = activeChannel?.channel_type === 'group';
    const confirmMessage = isGroup
      ? `Delete "${chatLabel}"? All members will lose access to this group chat.`
      : `Delete chat with ${chatLabel}? This removes the conversation from your list.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }
    setDeletingGroup(true);
    try {
      await discussionsApi.deleteChannel(activeChannelId);
      toast.success(isGroup ? 'Group deleted' : 'Chat deleted');
      setActiveChannelId(null);
      setMessages([]);
      setActiveMembers([]);
      if (isNarrow) setMobileScreen('list');
      await loadChannels();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete chat');
    } finally {
      setDeletingGroup(false);
    }
  };

  const startEditingGroupName = () => {
    if (!canManageActiveGroup || !activeChannel) return;
    setGroupNameDraft(activeChannel.name || '');
    setEditingGroupName(true);
  };

  const cancelEditingGroupName = () => {
    setEditingGroupName(false);
    setGroupNameDraft('');
  };

  const handleRenameGroup = async () => {
    if (!activeChannelId || !canManageActiveGroup) return;
    const nextName = groupNameDraft.trim();
    if (!nextName) {
      toast.error('Group name cannot be empty');
      return;
    }
    if (nextName === (activeChannel?.name || '').trim()) {
      cancelEditingGroupName();
      return;
    }
    setRenamingGroup(true);
    try {
      await discussionsApi.updateChannel(activeChannelId, { name: nextName });
      setChannels((prev) =>
        prev.map((c) =>
          Number(c.id) === Number(activeChannelId)
            ? { ...c, name: nextName, display_name: nextName }
            : c
        )
      );
      toast.success('Group name updated');
      cancelEditingGroupName();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to rename group');
    } finally {
      setRenamingGroup(false);
    }
  };

  useEffect(() => {
    if (!isNarrow) setMobileScreen('list');
  }, [isNarrow]);

  useEffect(() => {
    if (!attachMenuOpen) return undefined;
    const onPointerDown = (event) => {
      if (attachMenuRef.current && !attachMenuRef.current.contains(event.target)) {
        setAttachMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [attachMenuOpen]);

  if (loading) {
    return <div className="p-4 text-sm text-gray-500">Loading discussions...</div>;
  }

  const showListPanel = !isNarrow || mobileScreen === 'list';
  const showChatPanel = !isNarrow || mobileScreen === 'chat';

  return (
    <div className="flex min-h-[280px] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white max-md:h-[calc(100dvh-9.5rem)] md:h-[calc(100vh-10rem)]">
      <div className="grid min-h-0 flex-1 grid-cols-1 md:grid-cols-12">
        <div
          className={`flex min-h-0 flex-col overflow-hidden border-gray-200 md:col-span-3 md:border-r ${
            showListPanel ? 'flex' : 'hidden md:flex'
          }`}
        >
          <div className="flex shrink-0 items-center justify-between gap-2 border-b border-gray-200 px-3 py-3 sm:px-4">
            <span className="text-base font-semibold text-gray-900">Discussions</span>
            <button
              type="button"
              onClick={() => {
                setComposeOpen(true);
                setComposeMode('direct');
                setSelectedUsers([]);
                setGroupName('');
                setUserSearch('');
              }}
              className="inline-flex min-h-9 items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50"
            >
              <HiOutlinePlus className="h-4 w-4" />
              New
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {channels.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-gray-500">No chats yet. Start one with New.</p>
            ) : (
              channels.map((channel) => (
                <button
                  key={channel.id}
                  type="button"
                  onClick={() => openChannel(channel.id)}
                  className={`flex w-full items-start gap-3 border-b border-gray-100 px-3 py-3.5 text-left transition-colors active:bg-gray-100 sm:px-4 ${
                    activeChannelId === channel.id ? 'bg-primary-50' : 'hover:bg-gray-50'
                  }`}
                >
                  <span
                    className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
                      channel.channel_type === 'group' ? 'bg-sky-100 text-sky-700' : 'bg-violet-100 text-violet-700'
                    }`}
                  >
                    {channel.channel_type === 'group' ? (
                      <HiOutlineUserGroup className="h-5 w-5" />
                    ) : (
                      <HiOutlineUsers className="h-5 w-5" />
                    )}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold text-gray-900">
                      {getChannelLabel(channel)}
                    </span>
                    <span className="mt-0.5 block text-xs capitalize text-gray-500">
                      {channel.channel_type === 'direct' ? 'Direct message' : channel.channel_type}
                    </span>
                  </span>
                </button>
              ))
            )}
          </div>
        </div>

        <div
          className={`relative flex min-h-0 flex-col border-gray-200 md:col-span-6 md:border-r lg:border-r-0 ${
            showChatPanel ? 'flex flex-1' : 'hidden md:flex'
          }`}
        >
          <div className="flex shrink-0 items-center gap-2 border-b border-gray-200 px-2 py-2.5 sm:px-4 sm:py-3">
            {isNarrow && (
              <button
                type="button"
                onClick={backToChannelList}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-gray-600 hover:bg-gray-100 md:hidden"
                aria-label="Back to chats"
              >
                <HiOutlineArrowLeft className="h-5 w-5" />
              </button>
            )}
            <div className="min-w-0 flex-1">
              <h2 className="truncate text-base font-semibold text-gray-900 md:text-lg">
                {activeChannel ? activeChannelLabel : isNarrow && mobileScreen === 'chat' ? 'Chat' : 'Select a chat'}
              </h2>
              {activeChannel && (
                <p className="truncate text-xs capitalize text-gray-500">
                  {activeChannel.channel_type === 'direct' ? 'Direct message' : activeChannel.channel_type}
                </p>
              )}
            </div>
            {canManageActiveGroup && (
              <button
                type="button"
                onClick={startEditingGroupName}
                className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 sm:px-3 md:hidden"
                title="Rename group"
              >
                <HiOutlinePencil className="h-4 w-4" />
              </button>
            )}
            {canDeleteActiveChat && (
              <button
                type="button"
                onClick={handleDeleteChat}
                disabled={deletingGroup}
                className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-rose-200 bg-white px-2.5 py-1.5 text-xs font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-50 sm:px-3"
                title={activeChannel?.channel_type === 'group' ? 'Delete group' : 'Delete chat'}
              >
                <HiOutlineTrash className="h-4 w-4" />
                <span className="hidden sm:inline">
                  {deletingGroup
                    ? 'Deleting…'
                    : activeChannel?.channel_type === 'group'
                      ? 'Delete group'
                      : 'Delete chat'}
                </span>
              </button>
            )}
          </div>
          {editingGroupName && canManageActiveGroup && (
            <div className="border-b border-gray-200 bg-gray-50 px-3 py-3 sm:px-4 md:hidden">
              <div className="flex gap-2">
                <input
                  value={groupNameDraft}
                  onChange={(e) => setGroupNameDraft(e.target.value)}
                  maxLength={255}
                  className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Group name"
                />
                <button
                  type="button"
                  onClick={handleRenameGroup}
                  disabled={renamingGroup || !groupNameDraft.trim()}
                  className="rounded-lg bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={cancelEditingGroupName}
                  disabled={renamingGroup}
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain px-3 py-3 sm:px-4">
            {activeChannelId &&
              messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${Number(message.sender_id) === Number(user?.id) ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-3 py-2 shadow-sm sm:max-w-[82%] ${
                      Number(message.sender_id) === Number(user?.id)
                        ? 'rounded-br-md bg-primary-600 text-white'
                        : 'rounded-bl-md bg-gray-100 text-gray-900'
                    }`}
                  >
                    <p
                      className={`text-xs ${
                        Number(message.sender_id) === Number(user?.id) ? 'text-primary-100' : 'text-gray-500'
                      }`}
                    >
                      {Number(message.sender_id) === Number(user?.id) ? 'You' : message.sender_name}
                    </p>
                    {message.content_text ? (
                      <p
                        className={`text-sm ${
                          Number(message.sender_id) === Number(user?.id) ? 'text-white' : 'text-gray-900'
                        }`}
                      >
                        {renderMessageText(message.content_text)}
                      </p>
                    ) : null}
                    {Array.isArray(message.attachments) && message.attachments.length > 0 && (
                      <div className="mt-2 space-y-2">
                        {message.attachments.map((file, idx) => (
                          <MessageAttachment
                            key={`${message.id}-${file.id || idx}`}
                            file={file}
                            isOwn={Number(message.sender_id) === Number(user?.id)}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            {!activeChannelId && (
              <div className="flex flex-col items-center justify-center px-4 py-12 text-center text-sm text-gray-500 md:py-16">
                <p className="max-w-xs md:max-w-md">
                  Choose a conversation from the list, or tap <span className="font-medium text-gray-700">New</span> to
                  start one.
                </p>
              </div>
            )}
          </div>
          <div
            className="sticky bottom-0 z-10 border-t border-gray-200 bg-white p-2 shadow-[0_-4px_20px_rgba(0,0,0,0.06)] sm:p-3"
            style={{ paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom, 0px))' }}
          >
            {pendingAttachments.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-2">
                {pendingAttachments.map((file, idx) => (
                  <div key={`${file.object_key}-${idx}`} className="relative rounded-lg border border-gray-200 bg-gray-50 p-1">
                    <PendingAttachmentPreview file={file} />
                    <button
                      type="button"
                      onClick={() => setPendingAttachments((prev) => prev.filter((_, i) => i !== idx))}
                      className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 hover:text-gray-700"
                      aria-label="Remove attachment"
                    >
                      <HiX className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-end gap-1.5 sm:gap-2">
              <input
                value={text}
                onChange={(e) => onTextChange(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                disabled={!activeChannelId}
                placeholder={activeChannelId ? 'Message… @name to mention' : 'Select a chat'}
                className="min-h-[44px] flex-1 rounded-xl border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-50"
              />
              <input
                ref={photoInputRef}
                type="file"
                className="hidden"
                onChange={onPickAttachment}
                accept="image/*"
              />
              <input
                ref={videoInputRef}
                type="file"
                className="hidden"
                onChange={onPickAttachment}
                accept="video/*,.mp4,.webm,.mov,.m4v,.avi,.mkv,.3gp"
              />
              <input
                ref={documentInputRef}
                type="file"
                className="hidden"
                onChange={onPickAttachment}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,text/csv"
              />
              <div className="relative" ref={attachMenuRef}>
                <button
                  type="button"
                  onClick={() => setAttachMenuOpen((open) => !open)}
                  disabled={!activeChannelId || uploadingAttachment}
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 sm:w-auto sm:gap-1 sm:px-3"
                  title="Attach"
                >
                  <HiOutlinePaperClip className="h-5 w-5" />
                  <span className="hidden sm:inline">{uploadingAttachment ? '…' : 'Attach'}</span>
                </button>
                {attachMenuOpen && activeChannelId && (
                  <div className="absolute bottom-full right-0 z-30 mb-2 w-44 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg">
                    <button
                      type="button"
                      onClick={() => openAttachmentPicker('photo')}
                      className="flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm text-gray-800 hover:bg-gray-50"
                    >
                      <HiOutlinePhotograph className="h-5 w-5 text-sky-600" />
                      Photo
                    </button>
                    <button
                      type="button"
                      onClick={() => openAttachmentPicker('video')}
                      className="flex w-full items-center gap-3 border-t border-gray-100 px-3 py-2.5 text-left text-sm text-gray-800 hover:bg-gray-50"
                    >
                      <HiOutlineFilm className="h-5 w-5 text-violet-600" />
                      Video
                    </button>
                    <button
                      type="button"
                      onClick={() => openAttachmentPicker('document')}
                      className="flex w-full items-center gap-3 border-t border-gray-100 px-3 py-2.5 text-left text-sm text-gray-800 hover:bg-gray-50"
                    >
                      <HiOutlineDocumentText className="h-5 w-5 text-amber-600" />
                      Document
                    </button>
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={sendMessage}
                disabled={!activeChannelId}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 sm:w-auto sm:gap-1 sm:px-4"
                title="Send"
              >
                <HiOutlinePaperAirplane className="h-5 w-5" />
                <span className="hidden sm:inline">Send</span>
              </button>
            </div>
            {mentionOpen && mentionOptions.length > 0 && (
              <div className="absolute bottom-full left-2 right-2 z-20 mb-2 max-h-56 overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-lg sm:left-3 sm:right-auto sm:w-72">
                {mentionOptions.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => onSelectMention(m)}
                    className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                  >
                    <p className="text-sm text-gray-900">{m.full_name}</p>
                    <p className="text-xs text-gray-500">{m.email}</p>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="hidden border-l border-gray-200 p-4 md:col-span-3 md:block md:overflow-y-auto">
          <h3 className="text-sm font-semibold text-gray-800 mb-2">Details</h3>
          {activeChannel ? (
            <div className="space-y-3">
              <div>
                <div className="mb-1 flex items-center justify-between gap-2">
                  <p className="text-xs font-medium text-gray-500">Name</p>
                  {canManageActiveGroup && !editingGroupName && (
                    <button
                      type="button"
                      onClick={startEditingGroupName}
                      className="inline-flex items-center gap-1 text-xs font-medium text-primary-700 hover:text-primary-800"
                    >
                      <HiOutlinePencil className="h-3.5 w-3.5" />
                      Rename
                    </button>
                  )}
                </div>
                {editingGroupName ? (
                  <div className="space-y-2">
                    <input
                      value={groupNameDraft}
                      onChange={(e) => setGroupNameDraft(e.target.value)}
                      maxLength={255}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="Group name"
                    />
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={handleRenameGroup}
                        disabled={renamingGroup || !groupNameDraft.trim()}
                        className="flex-1 rounded-lg bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                      >
                        {renamingGroup ? 'Saving…' : 'Save'}
                      </button>
                      <button
                        type="button"
                        onClick={cancelEditingGroupName}
                        disabled={renamingGroup}
                        className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-900">{activeChannelLabel}</p>
                )}
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500">Type</p>
                <p className="text-sm capitalize text-gray-900">
                  {activeChannel.channel_type === 'direct' ? 'Direct message' : activeChannel.channel_type}
                </p>
              </div>
              {activeMembers.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-1">Members ({activeMembers.length})</p>
                  <ul className="space-y-1">
                    {activeMembers.map((member) => (
                      <li key={member.id} className="text-sm text-gray-800">
                        {member.full_name}
                        {member.member_role !== 'member' && (
                          <span className="ml-1 text-xs capitalize text-gray-500">({member.member_role})</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {canDeleteActiveChat && (
                <button
                  type="button"
                  onClick={handleDeleteChat}
                  disabled={deletingGroup}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-medium text-rose-700 hover:bg-rose-100 disabled:opacity-50"
                >
                  <HiOutlineTrash className="h-4 w-4" />
                  {deletingGroup
                    ? 'Deleting…'
                    : activeChannel.channel_type === 'group'
                      ? 'Delete group'
                      : 'Delete chat'}
                </button>
              )}
            </div>
          ) : (
            <p className="text-xs text-gray-600">Select a chat to see members and options.</p>
          )}
          <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
            @mention triggers real-time bell notifications and modal updates.
          </div>
        </div>
      </div>
      {composeOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setComposeOpen(false)}>
          <div className="w-full max-w-xl rounded-xl bg-white border border-gray-200 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900">Start conversation</h3>
              <button onClick={() => setComposeOpen(false)} className="text-gray-500 hover:text-gray-700">
                <HiX className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div className="flex gap-2">
                <button onClick={() => { setComposeMode('direct'); setSelectedUsers([]); }} className={`px-3 py-1.5 text-sm rounded-lg border ${composeMode === 'direct' ? 'bg-primary-600 text-white border-primary-600' : 'border-gray-300'}`}>Direct</button>
                <button onClick={() => { setComposeMode('group'); setSelectedUsers([]); }} className={`px-3 py-1.5 text-sm rounded-lg border ${composeMode === 'group' ? 'bg-primary-600 text-white border-primary-600' : 'border-gray-300'}`}>Group</button>
              </div>
              {composeMode === 'group' && (
                <input
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  placeholder="Enter group name"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              )}
              <input
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                placeholder="Search users..."
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
              <div className="max-h-72 overflow-y-auto border border-gray-200 rounded-lg">
                {filteredUsers.map((u) => {
                  const checked = selectedUsers.includes(u.id);
                  return (
                    <label key={u.id} className="flex items-center gap-3 px-3 py-2 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 cursor-pointer">
                      <input
                        type={composeMode === 'direct' ? 'radio' : 'checkbox'}
                        checked={checked}
                        onChange={() => {
                          if (composeMode === 'direct') {
                            setSelectedUsers([u.id]);
                          } else {
                            toggleUser(u.id);
                          }
                        }}
                      />
                      <div className="min-w-0">
                        <p className="text-sm text-gray-900 truncate">{u.full_name}</p>
                        <p className="text-xs text-gray-500 truncate">{u.email} · {u.role_name}</p>
                      </div>
                    </label>
                  );
                })}
              </div>
              <button
                onClick={createConversation}
                disabled={composeMode === 'direct' ? selectedUsers.length !== 1 : (!groupName.trim() || selectedUsers.length < 2)}
                className="w-full rounded-lg bg-primary-600 text-white py-2 text-sm disabled:opacity-50"
              >
                {composeMode === 'direct' ? 'Start direct chat' : 'Create group'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Discussions;
